import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { GeneralRequestService } from '../../services/general-request.service';
import { DuplicateRequestCheckDto, GeneralRequestSaveDto } from '../../models/general-request-models';
import { LookupService } from '../../../admin/services/lookup.service';
import { SelectList } from '../../../../shared/models/select-list';
import { PartnerService } from '../../../association/services/partner.service';
import { ApiResponse } from '../../../../shared/models/api-response.model';
import { BaseComponent } from '../../../../shared/components/component-base';
import { IBreadCrumb } from '../../../../shared/components/bread-crump/model/ibread-crumb';

/**
 * Custom validator to ensure text contains meaningful content
 * Rejects strings that are only special characters or too short
 */
function meaningfulTextValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) return null; // Let required validator handle empty

    // Trim whitespace
    const trimmed = value.trim();

    // Minimum length check is handled by minLength validator, but we also check here
    // Check if string contains at least some letters or numbers (not just special chars)
    // Pattern requires at least 3 consecutive alphanumeric characters
    // Supports English letters, Arabic letters (؀-ۿ), and numbers
    const hasAlphanumeric = /[a-zA-Z؀-ۿ0-9]{3,}/.test(trimmed);

    if (!hasAlphanumeric) {
      return { meaningfulText: true };
    }

    return null;
  };
}

@Component({
  selector: 'app-submit-request',
  templateUrl: './submit-request.component.html',
  styleUrls: ['./submit-request.component.css']
})
export class SubmitRequestComponent extends BaseComponent implements OnInit {
  pageTitle: string = '';
  breadcrumbItems: IBreadCrumb[] = [];
  form!: FormGroup;
  isSaving = false;
  isSubmitted = false;
  isEditMode = false;
  requestId: string | null = null;
  isCheckingDuplicate = false;
  duplicateCheckResult: DuplicateRequestCheckDto | null = null;

  requestTypes: SelectList[] = [];
  partners: SelectList[] = [];

  constructor(
    private fb: FormBuilder,
    private generalRequestService: GeneralRequestService,
    private lookupService: LookupService,
    private partnerService: PartnerService,
    private route: ActivatedRoute
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {


    

    const params = this.routeBase.snapshot.params;
    if (params && params['id']) {
      this.requestId = params['id'];
    }


    if (this.requestId) {
      this.isEditMode = true;
      console.log('Edit mode enabled for ID:', this.requestId);
    }

    this.initBreadcrumb();
    this.loadRequestTypes();
    this.loadPartners();

    if (this.requestId) {
      this.loadRequestForEdit();
    } else {
    }
  }

  initBreadcrumb() {
    this.pageTitle = this.isEditMode
      ? this.translate.instant("workflow.editRequest")
      : this.translate.instant("workflow.submitRequest");
    this.breadcrumbItems = [
      { label: this.translate.instant("common.approvalsLog"), url: "/common/approvals-history", isActive: false },
      { label: this.pageTitle, isActive: true }
    ];
  }

  initializeForm(): void {
    const today = new Date().toISOString().split('T')[0];
    this.form = this.fb.group({
      requestDate: [today, Validators.required],
      generalRequestTypeId: ['', Validators.required],
      requestedPartnerId: ['', Validators.required],
      partnerActivity: ['', [Validators.required, Validators.minLength(10), meaningfulTextValidator()]],
      partnershipDescription: ['', [Validators.required, Validators.minLength(20), meaningfulTextValidator()]],
      expectedImpact: ['', [Validators.required, Validators.minLength(20), meaningfulTextValidator()]],
      partnershipUnitOpinion: ['', [Validators.required, Validators.minLength(20), meaningfulTextValidator()]]
    });
  }

  loadRequestTypes(): void {
    this.lookupService.getSelectList('GeneralRequestType').subscribe({
      next: (res: any) => {
        if (res.success && res.value) {
          this.requestTypes = res.value;
        }
      },
      error: (error) => {
        console.error('Error loading request types:', error);
      }
    });
  }

  loadPartners(): void {
    this.partnerService.getSelectList().subscribe({
      next: (res) => {
        if (res.success && res.value) {
          this.partners = res.value;
        }
      },
      error: (error) => {
        console.error('Error loading partners:', error);
      }
    });
  }

  onPartnerChange(partnerId: string): void {
    if (partnerId) {
      this.generalRequestService.getPartnerActivity(partnerId).subscribe({
        next: (res: ApiResponse<string>) => {
          if (res.success) {
            this.form.patchValue({
              partnerActivity: res.value || ''
            });
          }
        },
        error: (error) => {
          console.error('Error loading partner activity:', error);
        }
      });
    }
  }

  loadRequestForEdit(): void {
    console.log('loadRequestForEdit called with ID:', this.requestId);
    this.generalRequestService.getById(this.requestId!).subscribe({
      next: (response) => {
        console.log('Load request response:', response);
        if (response.success) {
          const request = response.value;
          console.log('Request data:', request);

          // Populate form with existing data - convert number to string for select
          const requestDate = new Date(request.requestDate).toISOString().split('T')[0];
          this.form.patchValue({
            requestDate: requestDate,
            generalRequestTypeId: String(request.generalRequestTypeId),
            requestedPartnerId: request.requestedPartnerId,
            partnerActivity: request.partnerActivity || '',
            partnershipDescription: request.partnershipDescription || '',
            expectedImpact: request.expectedImpact || '',
            partnershipUnitOpinion: request.partnershipUnitOpinion || ''
          });
          console.log('Form patched with data, form value:', this.form.value);
        } else {
          console.error('API returned success=false:', response.message);
          this.toastrService.error(response.message || this.translate.instant('common.errorContent'));
          this._router.navigate(['/common/approvals-history']);
        }
      },
      error: (error) => {
        console.error('Error loading request:', error);
        this.toastrService.error(this.translate.instant('common.errorContent'));
        this._router.navigate(['/common/approvals-history']);
      }
    });
  }

  async onSubmit(): Promise<void> {
    this.isSubmitted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Skip duplicate check for edit mode
    if (this.isEditMode) {
      await this.proceedWithSubmission();
      return;
    }

    // Check for duplicates in new mode
    const formValue = this.form.value;
    this.isCheckingDuplicate = true;

    this.generalRequestService.checkForDuplicateRequests(
      formValue.requestedPartnerId,
      formValue.generalRequestTypeId
    ).subscribe({
      next: (res) => {
        this.isCheckingDuplicate = false;
        if (res.success && res.value?.hasDuplicate) {
          this.duplicateCheckResult = res.value;
          this.showDuplicateBlockedMessage(res.value);
        } else {
          this.proceedWithSubmission();
        }
      },
      error: (error) => {
        this.isCheckingDuplicate = false;
        console.error('Error checking for duplicates:', error);
        // On error, allow submission to proceed (fail-open approach)
        this.proceedWithSubmission();
      }
    });
  }

  private showDuplicateBlockedMessage(duplicateInfo: DuplicateRequestCheckDto): void {
    const statusText = duplicateInfo.duplicateRequestStatus === 'Pending'
      ? this.translate.instant('workflow.pending')
      : this.translate.instant('workflow.approved');

    const message = this.translate.instant('workflow.duplicateRequestWarning', { status: statusText });

    this.errorSwal(
      this.translate.instant('common.warning'),
      message
    );
  }

  private async proceedWithSubmission(): Promise<void> {
    const confirmResult = await this.confirm(
      this.translate.instant('common.confirm'),
      this.translate.instant('common.yes'),
      this.translate.instant('workflow.sendRequestConfirm')
    );

    if (!confirmResult.isConfirmed) {
      return;
    }

    this.isSaving = true;
    const formValue = this.form.value;

    const dto: GeneralRequestSaveDto = {
      id: this.isEditMode ? this.requestId! : undefined,
      requestDate: new Date(formValue.requestDate).toISOString(),
      generalRequestTypeId: formValue.generalRequestTypeId,
      requestedPartnerId: formValue.requestedPartnerId,
      partnerActivity: formValue.partnerActivity || undefined,
      partnershipDescription: formValue.partnershipDescription || undefined,
      expectedImpact: formValue.expectedImpact || undefined,
      partnershipUnitOpinion: formValue.partnershipUnitOpinion || undefined
    };

    this.generalRequestService.save(dto).subscribe({
      next: (res) => {
        this.isSaving = false;
        if (res.success) {
          this.toastrService.success(
            this.isEditMode
              ? this.translate.instant('workflow.updateRequestSuccess')
              : this.translate.instant('workflow.submitRequestSuccess')
          );
         //if (this.isEditMode) {
            this._router.navigate(['/common/approvals-history']);
          //} else {
          //  this.form.reset();
          //  this.initializeForm();
          //}
        } else {
          this.toastrService.error(res.message || this.translate.instant('common.errorContent'));
        }
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error submitting request:', error);
        this.toastrService.error(this.translate.instant('common.errorContent'));
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.touched || this.isSubmitted));
  }

  onCancel(): void {
    if (this.isEditMode) {
      this._router.navigate(['/common/approvals-history']);
    } else {
      this.form.reset();
      this.initializeForm();
      this.isSubmitted = false;
    }
  }
}
